import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Landmark, Plus, HandCoins, Ban, Undo2, X, Pencil, Paperclip, BellRing, Settings2 } from "lucide-react";

const brl = (v) => (v ?? 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const fdate = (iso) => {
  if (!iso) return "—";
  try { return new Date(String(iso).length === 10 ? iso + "T12:00:00" : iso).toLocaleDateString("pt-BR"); }
  catch { return String(iso); }
};

const SHORTCUTS = ["", "Em aberto", "Vence hoje", "Vencida", "Paga", "Cancelada"];
const SHORTCUT_LABEL = { "": "Todas", "Vencida": "Vencidas", "Paga": "Pagas", "Cancelada": "Canceladas" };
const STATUS_CLS = {
  "Em aberto": "text-sky-400 border-sky-900/60",
  "Vencida": "text-red-400 border-red-900/60",
  "Parcialmente paga": "text-amber-400 border-amber-900/60",
  "Paga": "text-emerald-400 border-emerald-900/60",
  "Cancelada": "text-slate-500 border-slate-700",
};
const METHODS = ["Dinheiro", "Pix", "Cartão de débito", "Cartão de crédito", "Boleto", "Transferência bancária", "Prazo acordado"];
const EMPTY_FORM = { supplier: "", cnpj: "", description: "", purchase_date: "", due_date: "", amount: "", notes: "", document_number: "", category: "", payment_method: "" };

const inputCls =
  "mt-1.5 w-full bg-slate-900/70 border border-slate-700 rounded-md px-3 py-2.5 text-sm outline-none focus:border-orange-500 transition-colors placeholder:text-slate-600";
const labelCls = "text-xs uppercase tracking-wider text-slate-400";

export default function AdminContasPagar() {
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({ status: "", q: "", category: "", supplier: "", due_from: "", due_to: "", sort: "due_date", order: "asc" });
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [modal, setModal] = useState(null);       // payable em edição ou "new"
  const [form, setForm] = useState(EMPTY_FORM);
  const [pay, setPay] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", paid_at: "", method: "", note: "", reference: "" });
  const [cfg, setCfg] = useState(null);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [fileBusy, setFileBusy] = useState(false);

  const load = (f = filters) => {
    const params = new URLSearchParams();
    Object.entries(f).forEach(([k, v]) => v && params.set(k, v));
    api.get(`/payables?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch((err) => { setData({ items: [], summary: {} }); toast.error(formatApiError(err)); });
    api.get("/payables/categories").then(({ data }) => setCategories(data)).catch(() => {});
    api.get("/payables/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  };
  useEffect(() => { load(); }, [filters.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  const openNew = () => { setForm(EMPTY_FORM); setModal("new"); };
  const openEdit = (p) => {
    setForm({ supplier: p.supplier, cnpj: p.cnpj || "", description: p.description,
      purchase_date: p.purchase_date, due_date: p.due_date, amount: String(p.amount),
      notes: p.notes || "", document_number: p.document_number || "",
      category: p.category || "", payment_method: p.payment_method || "" });
    setModal(p);
  };

  const submitForm = async () => {
    setSaving(true);
    try {
      const body = { ...form, amount: Number(form.amount) || 0 };
      if (modal === "new") {
        await api.post("/payables", body);
        toast.success("Conta cadastrada.");
      } else {
        await api.put(`/payables/${modal.id}`, body);
        toast.success("Conta atualizada.");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const openPay = (p) => {
    setPay(p);
    setPayForm({ amount: String(p.amount_open ?? ""), paid_at: "", method: p.payment_method || "", note: "", reference: "" });
  };

  const submitPay = async () => {
    setSaving(true);
    try {
      await api.post(`/payables/${pay.id}/payments`, { ...payForm, amount: Number(payForm.amount) || 0 });
      toast.success("Pagamento registrado.");
      setPay(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const cancelPayable = async (p) => {
    const reason = window.prompt(`Cancelar a conta ${p.number} (${p.supplier})?\nJustificativa (obrigatória):`, "");
    if (reason === null) return;
    try {
      await api.post(`/payables/${p.id}/cancel`, { reason });
      toast.success("Conta cancelada.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const reversePayable = async (p) => {
    const reason = window.prompt(`Estornar os pagamentos desta conta (${brl(p.amount_paid)} pagos)?\nMotivo (obrigatório p/ auditoria):`, "");
    if (reason === null) return;
    try {
      await api.post(`/payables/${p.id}/reverse-payments`, { reason });
      toast.success("Pagamentos estornados — conta reaberta.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const uploadFile = async (payableId, file) => {
    if (!file) return;
    setFileBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/payables/${payableId}/files`, fd);
      toast.success("Anexo enviado.");
      setModal((m) => (m && m !== "new" ? { ...m, files: data.files } : m));
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFileBusy(false);
    }
  };

  const downloadFile = async (payableId, idx, name) => {
    try {
      const { data } = await api.get(`/payables/${payableId}/files/${idx}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = name || "anexo";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const openCfg = () => {
    api.get("/payables/notify-settings").then(({ data }) => { setCfg(data); setCfgOpen(true); })
      .catch((err) => toast.error(formatApiError(err)));
  };

  const saveCfg = async () => {
    try {
      await api.put("/payables/notify-settings", cfg);
      toast.success("Configuração salva.");
      setCfgOpen(false);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const runNotifyNow = async () => {
    try {
      const { data } = await api.post("/payables/notify-due", { force: true });
      toast.success(data.sent > 0
        ? `${data.sent} conta(s) notificada(s) no sino do sistema.`
        : `Nenhuma notificação enviada${data.skipped ? ` (${data.skipped})` : " — sem contas vencendo hoje em aberto"}.`);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (!data) {
    return <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-600" /></div>;
  }
  const s = data.summary || {};

  return (
    <div data-testid="payables-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="display text-3xl font-extrabold uppercase">Financeiro — Contas a Pagar</h1>
          <p className="mt-1 text-sm text-slate-500">
            Despesas e obrigações registradas manualmente — o sistema não movimenta dinheiro nem estoque.
          </p>
          <Link to="/financeiro" data-testid="payables-receber-link"
            className="mt-1 inline-block text-xs text-orange-400 hover:text-orange-300 uppercase tracking-wider transition-colors">
            ← Contas a Receber
          </Link>
        </div>
        <div className="flex gap-2">
          <button data-testid="payables-notify-run" onClick={runNotifyNow}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors">
            <BellRing className="w-3.5 h-3.5" /> Verificar vencimentos
          </button>
          <button data-testid="payables-notify-settings" onClick={openCfg}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 hover:border-orange-500/50 transition-colors">
            <Settings2 className="w-3.5 h-3.5" /> Notificações
          </button>
          <button data-testid="payables-new-button" onClick={openNew}
            className="inline-flex items-center gap-1.5 pill-accent rounded-md px-4 py-2 text-xs font-semibold uppercase tracking-wider transition">
            <Plus className="w-4 h-4" /> Nova conta
          </button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { label: "Em aberto", value: s.open_total, testId: "pay-summary-open", cls: "text-sky-400" },
          { label: "Vencem hoje", value: s.due_today_total, testId: "pay-summary-today", cls: "text-amber-400" },
          { label: "Vencidas", value: s.overdue_total, testId: "pay-summary-overdue", cls: "text-red-400" },
          { label: "Pago no mês", value: s.paid_month_total, testId: "pay-summary-paid", cls: "text-emerald-400" },
          { label: "Próximos 7 dias", value: s.next_7d_total, testId: "pay-summary-7d", cls: "text-slate-200" },
          { label: "Previsto no mês", value: s.month_forecast_total, testId: "pay-summary-month", cls: "text-slate-200" },
        ].map((c) => (
          <div key={c.label} className="card-tactical rounded-md p-4" data-testid={c.testId}>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className={`mt-1 mono text-lg font-bold ${c.cls}`}>{brl(c.value)}</div>
          </div>
        ))}
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="payables-shortcuts">
        {SHORTCUTS.map((st) => (
          <button key={st || "todas"} data-testid={`payables-shortcut-${st ? st.toLowerCase().replace(/ /g, "-") : "todas"}`}
            onClick={() => setF("status", st)}
            className={`rounded-md border px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider transition-colors ${
              filters.status === st ? "border-orange-500/60 text-orange-400" : "border-slate-700 text-slate-500 hover:text-slate-300"}`}>
            {SHORTCUT_LABEL[st] || st}
          </button>
        ))}
      </div>

      <div className="mt-3 flex flex-wrap gap-3">
        <input data-testid="payables-search" value={filters.q} onChange={(e) => setF("q", e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load()}
          placeholder="Fornecedor, CNPJ, descrição ou nº documento..." className={`${inputCls} !mt-0 flex-1 min-w-52`} />
        <select data-testid="payables-filter-category" value={filters.category} onChange={(e) => setF("category", e.target.value)} className={`${inputCls} !mt-0 w-44`}>
          <option value="">Categoria</option>
          {categories.map((c) => <option key={c}>{c}</option>)}
        </select>
        <select data-testid="payables-filter-supplier" value={filters.supplier} onChange={(e) => setF("supplier", e.target.value)} className={`${inputCls} !mt-0 w-48`}>
          <option value="">Fornecedor</option>
          {suppliers.map((c) => <option key={c}>{c}</option>)}
        </select>
        <input data-testid="payables-filter-from" type="date" value={filters.due_from} onChange={(e) => setF("due_from", e.target.value)} className={`${inputCls} !mt-0 w-40`} />
        <input data-testid="payables-filter-to" type="date" value={filters.due_to} onChange={(e) => setF("due_to", e.target.value)} className={`${inputCls} !mt-0 w-40`} />
        <select data-testid="payables-sort" value={`${filters.sort}:${filters.order}`}
          onChange={(e) => { const [sort, order] = e.target.value.split(":"); setFilters((f) => ({ ...f, sort, order })); }}
          className={`${inputCls} !mt-0 w-48`}>
          <option value="due_date:asc">Vencimento ↑</option>
          <option value="due_date:desc">Vencimento ↓</option>
          <option value="amount:desc">Valor ↓</option>
          <option value="amount:asc">Valor ↑</option>
        </select>
        <button data-testid="payables-search-button" onClick={() => load()}
          className="pill-accent rounded-md px-5 py-2 text-xs font-semibold uppercase tracking-wider transition">
          Buscar
        </button>
      </div>

      <div className="mt-4 card-tactical rounded-md overflow-x-auto">
        <table className="w-full text-sm" data-testid="payables-table">
          <thead>
            <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
              <th className="px-4 py-3">Vencimento</th>
              <th className="px-4 py-3">Fornecedor / Descrição</th>
              <th className="px-4 py-3">Compra</th>
              <th className="px-4 py-3">Categoria</th>
              <th className="px-4 py-3 text-right">Valor</th>
              <th className="px-4 py-3 text-right">Pago</th>
              <th className="px-4 py-3 text-right">Em aberto</th>
              <th className="px-4 py-3">Situação</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {(data.items || []).map((p) => {
              const dueToday = p.due_date === new Date().toLocaleDateString("en-CA");
              return (
                <tr key={p.id} data-testid={`payables-row-${p.id}`}
                  className={`border-b border-slate-800/50 hover:bg-slate-900/40 transition-colors ${
                    p.status === "Vencida" ? "bg-red-950/10" : dueToday && ["Em aberto", "Parcialmente paga"].includes(p.status) ? "bg-amber-950/10" : ""}`}>
                  <td className="px-4 py-3 mono text-xs text-slate-300">
                    {fdate(p.due_date)}
                    {p.paid_at && <div className="text-[10px] text-emerald-500">paga em {fdate(p.paid_at)}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-slate-200">
                      {p.supplier}
                      {(p.files || []).length > 0 && <Paperclip className="inline w-3 h-3 ml-1 text-slate-500" />}
                    </div>
                    <div className="text-[10px] text-slate-500">{p.description}</div>
                    <div className="mono text-[10px] text-slate-600">
                      {p.number}{p.cnpj ? ` · ${p.cnpj}` : ""}{p.document_number ? ` · doc ${p.document_number}` : ""}
                    </div>
                  </td>
                  <td className="px-4 py-3 mono text-xs text-slate-400">{fdate(p.purchase_date)}</td>
                  <td className="px-4 py-3 text-xs text-slate-400">{p.category || "—"}</td>
                  <td className="px-4 py-3 mono text-xs text-right text-slate-200">{brl(p.amount)}</td>
                  <td className="px-4 py-3 mono text-xs text-right text-emerald-400">{brl(p.amount_paid)}</td>
                  <td className="px-4 py-3 mono text-xs text-right text-orange-400">{brl(p.amount_open)}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase tracking-wider border rounded-sm px-1.5 py-0.5 ${STATUS_CLS[p.status] || ""}`}
                      data-testid={`payables-status-${p.id}`}>{p.status}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-3">
                      {["Em aberto", "Vencida", "Parcialmente paga"].includes(p.status) && (
                        <button data-testid={`payables-pay-${p.id}`} onClick={() => openPay(p)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-emerald-400 transition-colors">
                          <HandCoins className="w-3 h-3" /> Pagar
                        </button>
                      )}
                      {!["Paga", "Cancelada"].includes(p.status) && (
                        <button data-testid={`payables-edit-${p.id}`} onClick={() => openEdit(p)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-orange-400 transition-colors">
                          <Pencil className="w-3 h-3" /> Editar
                        </button>
                      )}
                      {p.has_payments && (
                        <button data-testid={`payables-reverse-${p.id}`} onClick={() => reversePayable(p)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-amber-400 transition-colors">
                          <Undo2 className="w-3 h-3" /> Estornar
                        </button>
                      )}
                      {p.status !== "Cancelada" && (
                        <button data-testid={`payables-cancel-${p.id}`} onClick={() => cancelPayable(p)}
                          className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 hover:text-red-400 transition-colors">
                          <Ban className="w-3 h-3" /> Cancelar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {!(data.items || []).length && (
              <tr><td colSpan="9" className="px-4 py-10 text-center text-xs text-slate-600">
                <Landmark className="w-5 h-5 mx-auto mb-1.5 text-slate-800" strokeWidth={1.25} />
                Nenhuma conta a pagar encontrada.
              </td></tr>
            )}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setModal(null)}>
          <div className="card-tactical rounded-md p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
            data-testid="payable-modal">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-extrabold uppercase">{modal === "new" ? "Nova conta a pagar" : `Editar ${modal.number}`}</h2>
              <button onClick={() => setModal(null)} data-testid="payable-modal-close" className="text-slate-500 hover:text-slate-300" aria-label="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className={labelCls}>Empresa / Fornecedor *</label>
                <input data-testid="payable-supplier-input" value={form.supplier} list="payable-suppliers-list"
                  onChange={(e) => setForm({ ...form, supplier: e.target.value })} className={inputCls} />
                <datalist id="payable-suppliers-list">{suppliers.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div>
                <label className={labelCls}>CNPJ (opcional)</label>
                <input data-testid="payable-cnpj-input" value={form.cnpj}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })} className={inputCls} placeholder="00.000.000/0000-00" />
              </div>
              <div>
                <label className={labelCls}>Nº documento / NF</label>
                <input data-testid="payable-doc-input" value={form.document_number}
                  onChange={(e) => setForm({ ...form, document_number: e.target.value })} className={inputCls} />
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Descrição da despesa *</label>
                <input data-testid="payable-description-input" value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Data da compra *</label>
                <input data-testid="payable-purchase-date-input" type="date" value={form.purchase_date}
                  onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Vencimento *</label>
                <input data-testid="payable-due-date-input" type="date" value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Valor (R$) *</label>
                <input data-testid="payable-amount-input" type="number" step="0.01" min="0.01" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Categoria</label>
                <input data-testid="payable-category-input" value={form.category} list="payable-categories-list"
                  onChange={(e) => setForm({ ...form, category: e.target.value })} className={inputCls} />
                <datalist id="payable-categories-list">{categories.map((c) => <option key={c} value={c} />)}</datalist>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Forma de pagamento prevista</label>
                <select data-testid="payable-method-select" value={form.payment_method}
                  onChange={(e) => setForm({ ...form, payment_method: e.target.value })} className={inputCls}>
                  <option value="">—</option>
                  {METHODS.map((m) => <option key={m}>{m}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className={labelCls}>Observações</label>
                <textarea data-testid="payable-notes-input" rows={2} value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })} className={`${inputCls} resize-y`} />
              </div>
              {modal !== "new" && (
                <div className="col-span-2">
                  <label className={labelCls}>Anexos (NF, boleto, comprovante)</label>
                  <ul className="mt-1 space-y-1">
                    {(modal.files || []).map((f, i) => (
                      <li key={i}>
                        <button data-testid={`payable-file-${i}`} onClick={() => downloadFile(modal.id, i, f.name)}
                          className="text-xs text-orange-400 hover:text-orange-300 mono transition-colors">
                          {f.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                  <label className="mt-2 inline-flex items-center gap-1.5 rounded-md border border-slate-700 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400 hover:text-orange-400 cursor-pointer transition-colors">
                    {fileBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Paperclip className="w-3 h-3" />}
                    Anexar arquivo
                    <input data-testid="payable-file-input" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" className="hidden"
                      onChange={(e) => { uploadFile(modal.id, e.target.files[0]); e.target.value = ""; }} />
                  </label>
                </div>
              )}
            </div>
            <button data-testid="payable-submit" onClick={submitForm} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {modal === "new" ? "Cadastrar conta" : "Salvar alterações"}
            </button>
          </div>
        </div>
      )}

      {pay && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setPay(null)}>
          <div className="card-tactical rounded-md p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}
            data-testid="payable-pay-modal">
            <div className="flex items-center justify-between">
              <h2 className="display text-lg font-extrabold uppercase">Registrar pagamento</h2>
              <button onClick={() => setPay(null)} className="text-slate-500 hover:text-slate-300" aria-label="Fechar">
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500 mono">
              {pay.supplier} · {pay.number} · em aberto {brl(pay.amount_open)}
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className={labelCls}>Valor pago (R$)</label>
                <input data-testid="payable-pay-amount" type="number" step="0.01" min="0.01" value={payForm.amount}
                  onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} className={inputCls} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Data do pagamento</label>
                  <input data-testid="payable-pay-date" type="date" value={payForm.paid_at}
                    onChange={(e) => setPayForm({ ...payForm, paid_at: e.target.value })} className={inputCls} />
                </div>
                <div>
                  <label className={labelCls}>Forma</label>
                  <select data-testid="payable-pay-method" value={payForm.method}
                    onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className={inputCls}>
                    <option value="">—</option>
                    {METHODS.map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Observação</label>
                <input data-testid="payable-pay-note" value={payForm.note}
                  onChange={(e) => setPayForm({ ...payForm, note: e.target.value })} className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Referência (comprovante — evita registro duplicado)</label>
                <input data-testid="payable-pay-ref" value={payForm.reference}
                  onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} className={inputCls} />
              </div>
            </div>
            <button data-testid="payable-pay-submit" onClick={submitPay} disabled={saving}
              className="mt-5 inline-flex items-center gap-2 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider disabled:opacity-60 transition">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <HandCoins className="w-4 h-4" />}
              Confirmar pagamento
            </button>
          </div>
        </div>
      )}

      {cfgOpen && cfg && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setCfgOpen(false)}>
          <div className="card-tactical rounded-md p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}
            data-testid="payables-cfg-modal">
            <h2 className="display text-lg font-extrabold uppercase">Notificações de vencimento</h2>
            <p className="mt-1 text-xs text-slate-500">Rotina automática diária (horário de Brasília), sem precisar de tela aberta.</p>
            <label className="mt-4 flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
              <input data-testid="payables-cfg-enabled" type="checkbox" checked={cfg.enabled}
                onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                className="accent-orange-500 w-4 h-4" />
              Notificar contas que vencem no dia
            </label>
            <div className="mt-3">
              <label className={labelCls}>Horário (padrão 8h)</label>
              <input data-testid="payables-cfg-hour" type="number" min="0" max="23" value={cfg.hour}
                onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })} className={inputCls} />
            </div>
            <button data-testid="payables-cfg-save" onClick={saveCfg}
              className="mt-5 pill-accent rounded-md px-6 py-2.5 text-sm font-semibold uppercase tracking-wider transition">
              Salvar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
